import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { ContractSignatureType } from 'src/app/domain/enums/contract-signature-type.enum';
import { IPartnerContractRole } from 'src/app/domain/registration/interfaces/contract-type.interface';
import { IContractSignature } from 'src/app/domain/registration/models/IContractSignature';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { extractDocumentFromEntity, formatDocument } from 'src/app/shared/utils/document-formatter';
import { PartnerContractService } from '../../services/partner-contract.service';
import { ContractRoleSelectionComponent } from '../contract-role-selection/contract-role-selection.component';
import { AddSignatureDialogComponent } from '../../../contracts/components/contract-dialog/add-signature-dialog/add-signature-dialog.component';
import { ContractPreviewDialogComponent, ContractPreviewDialogData } from '../../../contracts/components/contract-dialog/contract-preview-dialog/contract-preview-dialog.component';
import { ContractGenerationService } from '../../../contracts/services/contract-generation.service';

@Component({
  selector: 'app-partner-contract-generate',
  templateUrl: './contract-generate.component.html',
  styleUrls: ['./contract-generate.component.sass']
})
export class PartnerContractGenerateComponent implements OnInit {
  @ViewChild(ContractRoleSelectionComponent) roleSelectionComponent?: ContractRoleSelectionComponent;

  signatures = new MatTableDataSource<IContractSignature>([]);
  displayedColumns: string[] = ['name', 'email', 'document', 'action', 'actions'];
  signatureTypes = ContractSignatureType;
  loading = false;
  isLoading = false;
  defaultContractedCustomer: any;
  partnerData: any;
  selectedRole: IPartnerContractRole | null = null;

  constructor(
    public dialogRef: MatDialogRef<PartnerContractGenerateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { entity: any, type: 'partner' | 'customer' },
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private authService: AuthService,
    private toastService: ToastService,
    private contractGenerationService: ContractGenerationService,
    private partnerContractService: PartnerContractService
  ) {
    const partner = data?.entity;
    this.partnerData = partner;

    if (!partner) {
      this.toastService.showError('Dados do parceiro não encontrados');
      this.dialogRef.close();
      return;
    }

    this.addEntitySignature(partner);
    this.addCurrentUserSignature();
    this.loadDefaultContractedCustomer();
  }

  ngOnInit(): void { }

  onRoleSelected(role: IPartnerContractRole): void {
    this.selectedRole = role;
    this.addPartnerAsSignatory();
    this.addDefaultCustomerAsSignatory();
  }

  private loadDefaultContractedCustomer(): void {
    this.isLoading = true;
    this.contractGenerationService.getDefaultContractedCustomer().subscribe({
      next: (defaultCustomer: any) => {
        this.isLoading = false;
        if (defaultCustomer && defaultCustomer.id) {
          this.defaultContractedCustomer = defaultCustomer;
          if (!this.signatures.data.some(s => s.isDefaultContracted)) {
            this.addContractedSignature(defaultCustomer);
          }
        } else {
          this.toastService.showWarn('Cliente padrão (contratada) não encontrado.');
        }
      },
      error: () => {
        this.isLoading = false;
        this.toastService.showError('Erro ao carregar dados da Contratada.');
      }
    });
  }

  private addContractedSignature(defaultCustomer: any): void {
    if (!defaultCustomer) return;

    if (this.signatures.data.some(s => s.isDefaultContracted)) return;

    const documento = extractDocumentFromEntity(defaultCustomer);
    const documentoFormatado = formatDocument(documento);

    const contractedSignature: IContractSignature = {
      code: 'CONTRATADA',
      name: defaultCustomer.name || defaultCustomer.companyName || defaultCustomer.tradingName || 'Mega Tech',
      email: defaultCustomer.email,
      document: documentoFormatado,
      whatsAppNumber: defaultCustomer.whatsAppNumber || '',
      type: ContractSignatureType.CONTRATANTE,
      status: 'PENDENTE',
      isContracted: false,
      isDefaultContracted: true,
      customerId: '0'
    };

    const currentData = this.signatures.data;
    if (!currentData.some(s => s.isDefaultContracted)) {
      this.signatures.data = [contractedSignature, ...currentData];
    }
  }

  private addEntitySignature(partner: any): void {
    if (!partner) return;

    const documento = extractDocumentFromEntity(partner);
    const documentoFormatado = formatDocument(documento);
    const telefone = partner.cellPhone || partner.phone || '';
    const nome = partner.name || partner.companyName || partner.tradingName || 'Parceiro';

    const entitySignature: IContractSignature = {
      code: '001',
      name: nome,
      email: partner.email || '',
      document: documentoFormatado,
      whatsAppNumber: telefone,
      type: ContractSignatureType.CONTRATADA,
      status: 'PENDENTE',
      isContracted: true,
      isDefaultContracted: false
    };

    if (!entitySignature.email) {
      this.toastService.showError('O parceiro não possui um email cadastrado.');
      this.dialogRef.close();
      return;
    }

    if (!documento) {
      this.toastService.showError('O parceiro não possui um documento (CPF/CNPJ) cadastrado.');
      this.dialogRef.close();
      return;
    }

    const currentSignatures = this.signatures.data;
    const contracted = currentSignatures.find(s => s.isDefaultContracted);
    this.signatures.data = contracted ? [contracted, entitySignature] : [entitySignature];
  }

  private addCurrentUserSignature(): void {
    const currentUser = this.authService.getUser();
    if (!currentUser) return;

    if (this.signatures.data.some(s => s.email?.toLowerCase() === currentUser.email?.toLowerCase())) {
      return;
    }

    const documento = extractDocumentFromEntity(currentUser);
    const documentoFormatado = formatDocument(documento);
    const telefone = currentUser.cellPhone || '';

    const userSignature: IContractSignature = {
      code: this.generateSignatureCode(),
      name: currentUser.name,
      email: currentUser.email,
      document: documentoFormatado,
      whatsAppNumber: telefone,
      type: ContractSignatureType.INTERVENIENTE,
      status: 'PENDENTE',
      isContracted: false,
      isDefaultContracted: false
    };

    if (!userSignature.email || !documento) {
      this.toastService.showWarn('Não foi possível adicionar o usuário atual como signatário.');
      return;
    }

    this.signatures.data = [...this.signatures.data, userSignature];
  }

  addSignature(): void {
    const dialogRef = this.dialog.open(AddSignatureDialogComponent, {
      width: '500px',
      data: {
        excludedTypes: [ContractSignatureType.CONTRATANTE, ContractSignatureType.CONTRATADA],
        allowedTypes: Object.values(ContractSignatureType).filter(
          t => typeof t === 'number' &&
            t !== ContractSignatureType.CONTRATANTE &&
            t !== ContractSignatureType.CONTRATADA
        )
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (this.signatures.data.some(s => s.email?.toLowerCase() === result.email?.toLowerCase())) {
          this.toastService.showWarn(`O email ${result.email} já foi adicionado.`);
          return;
        }

        const documento = result.document || '';
        const documentoFormatado = formatDocument(documento);

        const newSignature: IContractSignature = {
          code: this.generateSignatureCode(),
          name: result.name,
          email: result.email,
          document: documentoFormatado,
          type: result.type as ContractSignatureType,
          whatsAppNumber: result.whatsAppNumber || '',
          status: 'PENDENTE',
          isContracted: false,
          isDefaultContracted: false
        };

        this.signatures.data = [...this.signatures.data, newSignature];
      }
    });
  }

  editSignature(signature: IContractSignature, index: number): void {
    if (signature.isContracted || signature.isDefaultContracted) {
      this.toastService.showWarn('Não é possível editar o contratante principal ou a contratada.');
      return;
    }

    const dialogRef = this.dialog.open(AddSignatureDialogComponent, {
      width: '500px',
      data: {
        excludedTypes: [ContractSignatureType.CONTRATANTE, ContractSignatureType.CONTRATADA],
        allowedTypes: Object.values(ContractSignatureType).filter(
          t => typeof t === 'number' &&
            t !== ContractSignatureType.CONTRATANTE &&
            t !== ContractSignatureType.CONTRATADA
        ),
        signature: { ...signature }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (this.signatures.data.some((s, idx) => idx !== index && s.email?.toLowerCase() === result.email?.toLowerCase())) {
          this.toastService.showWarn(`O email ${result.email} já pertence a outro signatário.`);
          return;
        }

        const documento = result.document || signature.document || '';
        const documentoFormatado = formatDocument(documento);

        const updatedSignatures = [...this.signatures.data];
        updatedSignatures[index] = {
          ...signature,
          name: result.name,
          email: result.email,
          document: documentoFormatado,
          type: result.type as ContractSignatureType,
          whatsAppNumber: result.whatsAppNumber || signature.whatsAppNumber || '',
        };

        this.signatures.data = updatedSignatures;
      }
    });
  }

  removeSignature(index: number): void {
    const signatureToRemove = this.signatures.data[index];

    if (signatureToRemove.isDefaultContracted) {
      this.toastService.showWarn('A contratada principal não pode ser removida.');
      return;
    }

    if (signatureToRemove.isContracted) {
      this.toastService.showWarn('O contratante principal não pode ser removido.');
      return;
    }

    const updatedSignatures = [...this.signatures.data];
    updatedSignatures.splice(index, 1);
    this.signatures.data = updatedSignatures;
    this.toastService.showSuccess('Assinatura removida.');
  }

  private generateSignatureCode(index?: number): string {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 7);
    return `SIG_${index !== undefined ? index : ''}_${timestamp}_${randomPart}`.toUpperCase();
  }

  getSignatureTypeLabel(type: ContractSignatureType | undefined | string): string {
    if (type === undefined || type === null) return 'Indefinido';

    if (typeof type === 'string') {
      const enumValues = Object.values(ContractSignatureType);
      if (enumValues.includes(type as any)) {
        return type;
      }

      const enumKey = type.toUpperCase() as keyof typeof ContractSignatureType;
      if (ContractSignatureType[enumKey]) {
        const enumValue = ContractSignatureType[enumKey];
        return typeof enumValue === 'string' ? enumValue : enumKey.charAt(0) + enumKey.slice(1).toLowerCase();
      }
    }

    return typeof type === 'string' ? type : 'Desconhecido';
  }

  formatDate(date: Date | undefined): string {
    if (!date) return 'Não informado';
    return this.datePipe.transform(date, 'dd/MM/yyyy HH:mm:ss') || 'Formato inválido';
  }

  async generateContract(): Promise<void> {
    if (this.signatures.data.length === 0) {
      this.toastService.showError('Adicione pelo menos um signatário.');
      return;
    }

    if (!this.selectedRole) {
      this.toastService.showError('Selecione um tipo de contrato antes de gerar.');
      return;
    }

    const finalSignatures = this.signatures.data.map((sig, index) => ({
      ...sig,
      code: sig.code || this.generateSignatureCode(index + 1)
    }));

    const generationData = {
      partnerId: this.partnerData.id,
      contractType: this.selectedRole.type,
      signatures: finalSignatures
    };

    const previewData = {
      partnerId: this.partnerData.id,
      contractType: this.selectedRole.type,
      additionalSigners: finalSignatures
    };

    this.openPreviewModal(previewData, generationData);
  }

  private async proceedWithGeneration(generationData: any, previewDialogRef: MatDialogRef<ContractPreviewDialogComponent>): Promise<void> {
    try {
      if (previewDialogRef?.componentInstance) {
        previewDialogRef.componentInstance.setGenerating(true);
      }

      const result = await this.partnerContractService.generateContract(generationData);

      if (result && result.id) {
        this.toastService.showSuccess('Contrato gerado e enviado para assinaturas!');
        previewDialogRef.close(result);
        this.dialogRef.close(result);
      } else {
        throw new Error('Resposta inesperada da API');
      }
    } catch (error: any) {
      const errorMsg = error?.error?.message || error?.message || 'Erro desconhecido';
      this.toastService.showError(`Erro ao gerar contrato: ${errorMsg}`);
      previewDialogRef?.close();
    }
  }

  private openPreviewModal(previewData: any, generationData: any): void {
    const modalData: ContractPreviewDialogData = {
      contractTypeName: this.getContractTypeName(generationData.contractType),
      generationData,
      previewData,
      isPartnerPreview: true,
      useCustomTemplate: false
    };

    const dialogRef = this.dialog.open(ContractPreviewDialogComponent, {
      width: '80vw',
      height: '90vh',
      maxWidth: '95vw',
      maxHeight: '95vh',
      data: modalData,
    });

    const sub = dialogRef.componentInstance?.confirmGeneration.subscribe(genData => {
      this.proceedWithGeneration(genData, dialogRef);
    });

    dialogRef.afterClosed().subscribe(result => {
      sub?.unsubscribe();
      if (!result) {
        this.loading = false;
      }
    });
  }

  private getContractTypeName(type: string): string {
    const typeNames: Record<string, string> = {
      'partner': 'Parceiro',
      'consultant': 'Consultor',
      'representative': 'Representante',
      'counter': 'Contador',
      'unit': 'Unidade'
    };

    return typeNames[type.toLowerCase()] || type;
  }

  async removeExistingContract(role: IPartnerContractRole): Promise<void> {
    if (!role?.contractId) {
      this.toastService.showError('Informações do contrato não encontradas.');
      return;
    }

    this.loading = true;

    try {
      await lastValueFrom(this.partnerContractService.removeContract(role.contractId));
      this.toastService.showSuccess(`Contrato de ${role.label} removido com sucesso!`);

      if (this.selectedRole && this.selectedRole.type === role.type) {
        this.selectedRole = null;
      }

      await this.roleSelectionComponent?.loadContractRoles();
    } catch (error: any) {
      const errorMsg = error?.error?.message || error?.message || 'Erro desconhecido';
      this.toastService.showError(`Erro ao remover contrato: ${errorMsg}`);
    } finally {
      this.loading = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  getSignatureTypeColor(type: ContractSignatureType | undefined): string {
    if (!type) return '';

    if (typeof type === 'string') {
      const contractorTypes = [
        ContractSignatureType.PARCEIRO_REPRESENTANTE,
        ContractSignatureType.REPRESENTANTE_COMERCIAL,
        ContractSignatureType.PARCERIA_CONSULTORA,
        ContractSignatureType.CONTRATANTE
      ];

      if (contractorTypes.includes(type as any)) {
        return 'color-contractor';
      }

      if (type === ContractSignatureType.PARCEIRA_REPRESENTADA ||
        type === ContractSignatureType.CONTRATADA) {
        return 'color-contracted';
      }
    }

    return 'color-default';
  }

  getStatusLabel(status: string): string {
    const statusMap: Record<string, string> = {
      'processing': 'Em Processamento',
      'waiting_for_signatories': 'Aguardando Signatários',
      'waiting_for_signatures': 'Aguardando Assinaturas',
      'link_opened': 'Link Aberto',
      'signed': 'Assinado',
      'archived': 'Arquivado',
      'canceled': 'Cancelado',
      'editing': 'Em Edição',
      'draft': 'Rascunho',
      'expired': 'Expirado',
      'error': 'Erro'
    };

    return statusMap[status] || status;
  }

  private getPartnerRoleByContractType(contractType: string): string {
    const roleMap: Record<string, string> = {
      'partner': ContractSignatureType.PARCEIRO_REPRESENTANTE,
      'representative': ContractSignatureType.REPRESENTANTE_COMERCIAL,
      'consultant': ContractSignatureType.PARCERIA_CONSULTORA
    };

    return roleMap[contractType.toLowerCase()] || ContractSignatureType.CONTRATADA;
  }

  private addPartnerAsSignatory(): void {
    const partner = this.data.entity;
    if (!partner) return;

    const role = this.getPartnerRoleByContractType(this.selectedRole?.type || '');
    const partnerIndex = this.signatures.data.findIndex(
      s => s.email?.toLowerCase() === partner.email?.toLowerCase()
    );

    const documento = extractDocumentFromEntity(partner);
    const documentoFormatado = formatDocument(documento);

    const partnerSignature: IContractSignature = {
      code: this.generateSignatureCode(),
      name: partner.name || partner.companyName || 'Parceiro',
      email: partner.email,
      document: documentoFormatado,
      type: role as ContractSignatureType,
      role,
      status: 'PENDENTE',
      isContracted: true,
      whatsAppNumber: partner.cellPhone || partner.phone || ''
    };

    if (partnerIndex !== -1) {
      const updatedSignatures = [...this.signatures.data];
      updatedSignatures[partnerIndex] = {
        ...updatedSignatures[partnerIndex],
        ...partnerSignature
      };
      this.signatures.data = updatedSignatures;
    } else {
      this.signatures.data = [...this.signatures.data, partnerSignature];
    }
  }

  private addDefaultCustomerAsSignatory(): void {
    if (!this.defaultContractedCustomer) {
      this.contractGenerationService.getDefaultContractedCustomer().subscribe(customer => {
        this.defaultContractedCustomer = customer;
        this.addDefaultCustomerToSignatures();
      });
    } else {
      this.addDefaultCustomerToSignatures();
    }
  }

  private addDefaultCustomerToSignatures(): void {
    if (!this.defaultContractedCustomer) return;

    const customerIndex = this.signatures.data.findIndex(
      s => s.email?.toLowerCase() === this.defaultContractedCustomer.email?.toLowerCase()
    );

    const documento = extractDocumentFromEntity(this.defaultContractedCustomer);
    const documentoFormatado = formatDocument(documento);

    const customerSignature: IContractSignature = {
      code: this.generateSignatureCode(),
      name: this.defaultContractedCustomer.name || this.defaultContractedCustomer.companyName || 'Mega Tech',
      email: this.defaultContractedCustomer.email,
      document: documentoFormatado,
      type: ContractSignatureType.PARCEIRA_REPRESENTADA,
      role: ContractSignatureType.PARCEIRA_REPRESENTADA,
      status: 'PENDENTE',
      isDefaultContracted: true,
      customerId: '0',
      whatsAppNumber: this.defaultContractedCustomer.cellPhone || this.defaultContractedCustomer.phone || ''
    };

    if (customerIndex !== -1) {
      const updatedSignatures = [...this.signatures.data];
      updatedSignatures[customerIndex] = {
        ...updatedSignatures[customerIndex],
        ...customerSignature
      };
      this.signatures.data = updatedSignatures;
    } else {
      this.signatures.data = [...this.signatures.data, customerSignature];
    }
  }
}
